import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useAuthPrompt } from "@/context/AuthContext";
import { CATEGORIES, CURRENCIES, LOCATIONS, API } from "@/lib/constants";
import { RichTextEditor } from "@/components/shared/RichTextEditor";
import { MediaUpload } from "@/components/shared";
import { MessageCircle, TrendingUp, Heart, Plus, ChevronRight, ArrowLeft, ArrowRight, Loader2, User, Users, BarChart3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// START A CAMPAIGN strip + inline creators
// START A CAMPAIGN strip + inline creators
export const StartCampaignStrip = () => {
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [currency, setCurrency] = useState("NGN");
  const [duration, setDuration] = useState("30");
  const [continent, setContinent] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [fundraisingFor, setFundraisingFor] = useState("self");
  const [showTitle, setShowTitle] = useState(false);
  const [showCategory, setShowCategory] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [showPollPicker, setShowPollPicker] = useState(false);
  const [pollTemplates, setPollTemplates] = useState([]);
  const [recommendedPolls, setRecommendedPolls] = useState([]);
  const [visible, setVisible] = useState(true);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const fabMenuRef = useRef(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth } = useAuthPrompt();
  const selectedCurrency = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  // Position-based: strip visible at top, FAB visible when scrolled away
  useEffect(() => {
    if (expanded) { setVisible(true); return; }
    
    const THRESHOLD = 80; // pixels from top before switching to FAB
    const onScroll = () => {
      const atTop = window.scrollY <= THRESHOLD;
      setVisible(atTop);
      if (!atTop) setFabMenuOpen(false); // handled by scroll, keep menu closed during scroll
    };
    
    // Set initial state
    setVisible(window.scrollY <= THRESHOLD);
    
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [expanded]);

  // Fetch poll templates when needed
  useEffect(() => {
    if (selectedType === "discussion" || selectedType === "mock") {
      if (pollTemplates.length === 0) {
        axios.get(`${API}/poll-templates`).then(res => setPollTemplates(res.data.templates || [])).catch(() => {});
      }
    }
  }, [selectedType]);

  // Close FAB menu on outside click
  useEffect(() => {
    if (!fabMenuOpen) return;
    const onClickOutside = (e) => {
      if (fabMenuRef.current && !fabMenuRef.current.contains(e.target)) {
        setFabMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [fabMenuOpen]);

  const fetchPollRecommendations = () => {
    if (description && description.trim().length >= 10) {
      axios.post(`${API}/poll-templates/recommend`, { content: description }).then(res => {
        setRecommendedPolls(res.data.recommendations || []);
      }).catch(() => {});
    } else {
      setRecommendedPolls([]);
    }
  };

  const handleStripClick = () => {
    if (!user) {
      requireAuth();
      return;
    }
    setExpanded(!expanded);
  };

  const handleFabClick = () => {
    if (!user) {
      requireAuth();
      return;
    }
    setFabMenuOpen(prev => !prev);
  };

  const handleFabTypeSelect = (type) => {
    setFabMenuOpen(false);
    // Scroll to top, show strip, open form with selected type
    window.scrollTo({ top: 0, behavior: "auto" });
    setVisible(true);
    setExpanded(true);
    setSelectedType(type);
  };

  const uploadMedia = async (files) => {
    if (files.length === 0) return [];
    try {
      const formData = new FormData();
      for (const f of files) {
        formData.append("files", f.file, f.name);
      }
      const response = await axios.post(`${API}/media/upload`, formData, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: 30 * 1024 * 1024,
        maxBodyLength: 30 * 1024 * 1024
      });
      return (response.data.files || []).map(f => f.url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Media upload failed.");
      return [];
    }
  };

  const handleSubmit = async () => {
    if (!description.trim() && selectedType === "discussion") {
      toast.error("Please write your message");
      return;
    }
    if (selectedType !== "discussion" && (!title || !description.trim())) {
      toast.error("Please fill in title and description");
      return;
    }
    if (selectedType !== "discussion" && !targetAmount) {
      toast.error("Please enter a target funding amount");
      return;
    }

    setLoading(true);
    try {
      const mediaUrls = await uploadMedia(mediaFiles);
      const payload = {
        type: selectedType === "mock" ? "mock" : selectedType,
        title: title || (selectedType === "discussion" ? "" : title),
        description,
        category,
        currency,
        media: mediaUrls,
        ...(continent && country && { location: { continent, country } }),
        ...(selectedType !== "discussion" && { target_amount: parseFloat(targetAmount) }),
        ...(selectedType === "donation" && { duration_days: parseInt(duration), fundraising_for: fundraisingFor }),
        ...(selectedType === "mock" && { duration_days: parseInt(duration) }),
        ...(selectedPoll && { poll_template_id: selectedPoll.id })
      };

      const res = await axios.post(`${API}/topics`, payload, { withCredentials: true });
      const newTopicId = res.data?.topic?.id;
      toast.success("Campaign posted!");
      resetForm();
      if (newTopicId) {
        navigate(`/topic/${newTopicId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setExpanded(false);
    setTitle(""); setDescription(""); setCategory("");
    setTargetAmount(""); setMediaFiles([]);
    setShowTitle(false); setShowCategory(false);
    setSelectedPoll(null); setShowPollPicker(false);
    setContinent(""); setCountry("");
  };

  return (
    <>
    <div className="sticky top-12 lg:top-0 z-30">
      {/* The Strip - always mounted, dynamic "START YOUR CAMPAIGN" CTA */}
      <button
        onClick={handleStripClick}
        data-testid="start-campaign-strip"
        className={`w-full py-2 lg:py-3 px-4 text-center transition-all duration-300 ${
          expanded
            ? 'bg-gray-800 text-white font-extrabold text-sm tracking-widest uppercase'
            : 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 text-white hover:from-emerald-600 hover:via-emerald-500 hover:to-teal-600 shadow-lg hover:shadow-xl'
        }`}
        style={{
          opacity: (visible || expanded) ? 1 : 0,
          pointerEvents: (visible || expanded) ? 'auto' : 'none',
        }}
        aria-hidden={!(visible || expanded)}
      >
        {expanded ? (
          <span className="tracking-widest uppercase text-sm font-extrabold">CLOSE</span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <span className="font-heading text-base font-black tracking-wide uppercase" style={{letterSpacing: '0.15em'}}>Start Your Campaign</span>
            <ArrowRight size={18} className="animate-pulse" />
          </span>
        )}
      </button>

      {/* Campaign Type Selector */}
      {expanded && !selectedType && (
        <div className="bg-white border-b border-jeekota-border shadow-md animate-in slide-in-from-top-2 duration-200">
          <div className="max-w-2xl mx-auto p-3 space-y-2">
            {[
              { type: "donation", label: "Start a Donation Campaign", icon: Heart, color: "text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border-emerald-200" },
              { type: "mock", label: "Start a Validation Campaign", icon: TrendingUp, color: "text-amber-600 bg-amber-50 hover:bg-amber-100 border-amber-200" },
              { type: "discussion", label: "Start a Discussion", icon: MessageCircle, color: "text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-200" }
            ].map(opt => (
              <button
                key={opt.type}
                onClick={() => setSelectedType(opt.type)}
                data-testid={`campaign-type-${opt.type}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${opt.color}`}
              >
                <opt.icon size={20} />
                <span className="font-semibold text-sm">{opt.label}</span>
                <ChevronRight size={16} className="ml-auto" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Creator Form */}
      {expanded && selectedType && (
        <div className="bg-white border-b border-jeekota-border shadow-lg animate-in slide-in-from-top-2 duration-200 max-h-[70vh] overflow-y-auto">
          <div className="max-w-2xl mx-auto p-4 space-y-3">
            {/* Back to type selection */}
            <button onClick={() => { setSelectedType(null); setShowTitle(false); setShowCategory(false); }} className="text-xs text-jeekota-primary hover:underline flex items-center gap-1">
              <ArrowLeft size={14} /> Change campaign type
            </button>

            {/* Discussion: Twitter-like quick post */}
            {selectedType === "discussion" && (
              <>
                {/* Optional Title */}
                {showTitle ? (
                  <Input
                    data-testid="topic-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="h-10"
                  />
                ) : (
                  <button onClick={() => setShowTitle(true)} className="flex items-center gap-2 text-sm text-jeekota-text-secondary hover:text-jeekota-primary transition-colors">
                    <Plus size={16} className="border border-current rounded" /> Add a Title (optional)
                  </button>
                )}

                {/* Message body */}
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="What's on your mind? Type @ to mention someone..."
                  testId="topic-description-input"
                />

                {/* Optional Category */}
                {showCategory ? (
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="topic-category-select">
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <button onClick={() => setShowCategory(true)} className="flex items-center gap-2 text-sm text-jeekota-text-secondary hover:text-jeekota-primary transition-colors">
                    <Plus size={16} className="border border-current rounded" /> Add a Category (optional)
                  </button>
                )}

                {/* Optional Poll */}
                {selectedPoll ? (
                  <div className="flex items-center gap-2 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <BarChart3 size={16} className="text-indigo-600 shrink-0" />
                    <span className="text-sm font-medium text-indigo-700 flex-1">{selectedPoll.name}</span>
                    <span className="text-xs text-indigo-500">{selectedPoll.options.map(o => o.emoji).join(" ")}</span>
                    <button onClick={() => setSelectedPoll(null)} className="text-indigo-400 hover:text-indigo-600"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <button onClick={() => { setShowPollPicker(!showPollPicker); if (!showPollPicker) fetchPollRecommendations(); }}
                      className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors w-full"
                      data-testid="add-poll-btn">
                      <BarChart3 size={16} /> Add a Community Poll
                      <span className="ml-auto text-xs text-indigo-400 font-normal">AI picks for you</span>
                    </button>
                    {showPollPicker && (
                      <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg max-h-52 overflow-y-auto">
                        {recommendedPolls.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 bg-indigo-50 text-xs font-medium text-indigo-600">Recommended for your post</div>
                            {recommendedPolls.map(tpl => (
                              <button key={`rec-${tpl.id}`} onClick={() => { setSelectedPoll(tpl); setShowPollPicker(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors border-b border-gray-50 bg-indigo-50/30">
                                <span className="text-sm font-medium">{tpl.name}</span>
                                <span className="block text-xs text-jeekota-text-secondary mt-0.5">{tpl.options.map(o => `${o.emoji} ${o.label}`).join("  ·  ")}</span>
                              </button>
                            ))}
                            <div className="px-3 py-1.5 bg-gray-50 text-xs font-medium text-gray-500">All templates</div>
                          </>
                        )}
                        {pollTemplates.filter(tpl => !recommendedPolls.find(r => r.id === tpl.id)).map(tpl => (
                          <button key={tpl.id} onClick={() => { setSelectedPoll(tpl); setShowPollPicker(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-jeekota-primary/5 transition-colors border-b border-gray-50 last:border-0">
                            <span className="text-sm font-medium">{tpl.name}</span>
                            <span className="block text-xs text-jeekota-text-secondary mt-0.5">{tpl.options.map(o => `${o.emoji} ${o.label}`).join("  ·  ")}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Mock / Donation: Full form */}
            {selectedType !== "discussion" && (
              <>
                <Input data-testid="topic-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Campaign title *" />
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Describe your campaign..."
                  testId="topic-description-input"
                />
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Target Amount *</Label>
                    <Input type="number" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} placeholder="e.g., 100000" />
                  </div>
                  <div>
                    <Label className="text-xs">Currency</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger><SelectValue>{selectedCurrency.symbol} {selectedCurrency.code}</SelectValue></SelectTrigger>
                      <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Continent</Label>
                    <Select value={continent} onValueChange={(v) => { setContinent(v); setCountry(""); }}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{Object.keys(LOCATIONS).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Country</Label>
                    <Select value={country} onValueChange={setCountry} disabled={!continent}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-60">{continent && LOCATIONS[continent]?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {selectedType === "donation" && (
                  <>
                    <div>
                      <Label className="text-xs">Fundraising for</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {[
                          { value: "self", label: "Self", icon: User },
                          { value: "charity", label: "Charity", icon: Heart },
                          { value: "individual", label: "Individual", icon: Users }
                        ].map(opt => (
                          <button key={opt.value} type="button" onClick={() => setFundraisingFor(opt.value)}
                            className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all text-xs ${fundraisingFor === opt.value ? 'border-jeekota-primary bg-jeekota-primary/5 text-jeekota-primary' : 'border-gray-200 text-jeekota-text-secondary'}`}>
                            <opt.icon size={16} />{opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Duration</Label>
                      <Select value={duration} onValueChange={setDuration}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["7","14","30","60","90"].map(d => <SelectItem key={d} value={d}>{d} days</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {selectedType === "mock" && (
                  <div>
                    <Label className="text-xs">Campaign Duration</Label>
                    <Select value={duration} onValueChange={setDuration}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[{v:"7",l:"7 days"},{v:"14",l:"14 days"},{v:"30",l:"30 days"},{v:"60",l:"60 days"},{v:"90",l:"90 days"},{v:"180",l:"6 months"},{v:"365",l:"1 year"}].map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Optional Poll - Mock only */}
                {selectedType === "mock" && (
                  selectedPoll ? (
                    <div className="flex items-center gap-2 p-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <BarChart3 size={16} className="text-indigo-600 shrink-0" />
                      <span className="text-sm font-medium text-indigo-700 flex-1">{selectedPoll.name}</span>
                      <span className="text-xs text-indigo-500">{selectedPoll.options.map(o => o.emoji).join(" ")}</span>
                      <button onClick={() => setSelectedPoll(null)} className="text-indigo-400 hover:text-indigo-600"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <button onClick={() => { setShowPollPicker(!showPollPicker); if (!showPollPicker) fetchPollRecommendations(); }}
                        className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors w-full"
                        data-testid="add-poll-btn-mock">
                        <BarChart3 size={16} /> Add a Community Poll
                        <span className="ml-auto text-xs text-indigo-400 font-normal">AI picks for you</span>
                      </button>
                      {showPollPicker && (
                        <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg max-h-52 overflow-y-auto">
                          {recommendedPolls.length > 0 && (
                            <>
                              <div className="px-3 py-1.5 bg-indigo-50 text-xs font-medium text-indigo-600">Recommended for your post</div>
                              {recommendedPolls.map(tpl => (
                                <button key={`rec-${tpl.id}`} onClick={() => { setSelectedPoll(tpl); setShowPollPicker(false); }}
                                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 transition-colors border-b border-gray-50 bg-indigo-50/30">
                                  <span className="text-sm font-medium">{tpl.name}</span>
                                  <span className="block text-xs text-jeekota-text-secondary mt-0.5">{tpl.options.map(o => `${o.emoji} ${o.label}`).join("  ·  ")}</span>
                                </button>
                              ))}
                              <div className="px-3 py-1.5 bg-gray-50 text-xs font-medium text-gray-500">All templates</div>
                            </>
                          )}
                          {pollTemplates.filter(tpl => !recommendedPolls.find(r => r.id === tpl.id)).map(tpl => (
                            <button key={tpl.id} onClick={() => { setSelectedPoll(tpl); setShowPollPicker(false); }}
                              className="w-full text-left px-3 py-2 hover:bg-jeekota-primary/5 transition-colors border-b border-gray-50 last:border-0">
                              <span className="text-sm font-medium">{tpl.name}</span>
                              <span className="block text-xs text-jeekota-text-secondary mt-0.5">{tpl.options.map(o => `${o.emoji} ${o.label}`).join("  ·  ")}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}
              </>
            )}

            {/* Media upload - all types */}
            <div>
              <Label className="text-xs">Media (up to 6 images + 1 video)</Label>
              <MediaUpload onFilesChange={setMediaFiles} maxImages={6} maxVideos={1} />
            </div>

            {/* Submit */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={resetForm}>Cancel</Button>
              <Button
                data-testid="submit-topic-btn"
                size="sm"
                className="flex-1 bg-jeekota-primary hover:bg-jeekota-primary/90"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedType === "discussion" ? "Post" : "Create Campaign"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* FAB + popup menu - always mounted, CSS-only visibility toggle */}
    <div
      ref={fabMenuRef}
      className="fixed bottom-16 right-4 lg:bottom-8 lg:right-8 z-40 flex flex-col items-end gap-2"
      style={{
        opacity: (!visible && !expanded) ? 1 : 0,
        pointerEvents: 'none',
        transition: 'opacity 0.15s',
      }}
      aria-hidden={visible || expanded}
    >
      {/* Campaign type popup - appears above FAB */}
      <div
        data-testid="fab-campaign-menu"
        className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        style={{
          opacity: fabMenuOpen ? 1 : 0,
          transform: fabMenuOpen ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.9)',
          pointerEvents: (fabMenuOpen && !visible && !expanded) ? 'auto' : 'none',
          transition: 'opacity 0.2s, transform 0.2s',
          transformOrigin: 'bottom right',
        }}
      >
        <div className="p-2 space-y-1 min-w-[260px]">
          {[
            { type: "donation", label: "Donation", icon: Heart, color: "text-emerald-600 bg-emerald-50 hover:bg-emerald-100" },
            { type: "mock", label: "Validation", icon: TrendingUp, color: "text-amber-600 bg-amber-50 hover:bg-amber-100" },
            { type: "discussion", label: "Discussion", icon: MessageCircle, color: "text-blue-600 bg-blue-50 hover:bg-blue-100" }
          ].map(opt => (
            <button
              key={opt.type}
              onClick={() => handleFabTypeSelect(opt.type)}
              data-testid={`fab-type-${opt.type}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${opt.color}`}
            >
              <opt.icon size={18} />
              <span className="font-semibold text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* FAB button */}
      <button
        onClick={handleFabClick}
        data-testid="create-topic-fab"
        className={`w-14 h-14 rounded-full shadow-lg text-white flex items-center justify-center transition-all duration-200 pointer-events-auto ${
          fabMenuOpen ? 'bg-gray-800 rotate-45' : 'bg-emerald-500 hover:bg-emerald-600'
        }`}
      >
        <Plus size={24} />
      </button>
    </div>
    </>
  );
};
